import React from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import { useResponsive } from '../../../hooks/useResponsive';
import { MobileCardList } from './MobileCardList';
import type { ResponsiveColumnType, MobileCardConfig, MobileFieldRole } from './MobileCardList';

export type { ResponsiveColumnType, MobileCardConfig, MobileFieldRole };

/**
 * ResponsiveTable のプロパティ
 */
export interface ResponsiveTableProps<T extends object>
  extends Omit<TableProps<T>, 'columns'> {
  /** レスポンシブ対応のカラム定義 */
  columns: ResponsiveColumnType<T>[];
  /** モバイルカード表示の設定 */
  mobileCardConfig?: MobileCardConfig<T>;
  /** モバイル表示に切り替えるブレークポイント */
  breakpoint?: 'sm' | 'md';
  /** モバイル表示を無効化（常にテーブル表示） */
  disableMobileView?: boolean;
  /** デスクトップテーブルの横スクロール幅（デフォルト: 自動計算） */
  scrollX?: number | string | true;
}

/**
 * カラム幅から適切なスクロール幅を計算
 */
function calculateScrollX<T>(columns: ResponsiveColumnType<T>[]): number {
  let totalWidth = 0;
  let hasUnspecifiedWidth = false;

  columns.forEach((col) => {
    if (typeof col.width === 'number') {
      totalWidth += col.width;
    } else if (typeof col.width === 'string') {
      // パーセント指定やpx指定の場合は数値部分を抽出
      const numMatch = col.width.match(/^(\d+)/);
      if (numMatch) {
        totalWidth += parseInt(numMatch[1], 10);
      } else {
        hasUnspecifiedWidth = true;
      }
    } else {
      hasUnspecifiedWidth = true;
    }
  });

  // 幅未指定のカラムがある場合は最小幅を追加
  if (hasUnspecifiedWidth) {
    const unspecifiedCount = columns.filter(
      (col) => col.width === undefined
    ).length;
    totalWidth += unspecifiedCount * 150; // 未指定カラムは150pxと仮定
  }

  // 最小幅を保証
  return Math.max(totalWidth, 800);
}

/**
 * レスポンシブ対応テーブルコンポーネント
 *
 * デスクトップでは通常のAnt Design Tableを表示し、
 * モバイルではカードリスト形式で表示します。
 *
 * @example
 * ```tsx
 * <ResponsiveTable
 *   columns={[
 *     { title: '設備名', dataIndex: 'name', mobileRole: 'title' },
 *     { title: 'カテゴリ', dataIndex: 'category', mobileRole: 'tag' },
 *     { title: '健全度', dataIndex: 'health', mobileRole: 'description' },
 *     { title: '操作', render: () => <Button />, mobileRole: 'hidden' },
 *   ]}
 *   dataSource={data}
 *   mobileCardConfig={{
 *     actions: (record) => <Dropdown menu={...} />,
 *     onClick: (record) => navigate(`/detail/${record.id}`),
 *   }}
 * />
 * ```
 */
export function ResponsiveTable<T extends object>({
  columns,
  mobileCardConfig,
  breakpoint = 'md',
  disableMobileView = false,
  scrollX,
  pagination,
  loading,
  rowKey,
  dataSource,
  rowSelection,
  ...tableProps
}: ResponsiveTableProps<T>): React.ReactElement {
  const { isMobile, isSmallMobile } = useResponsive();

  // ブレークポイントに基づいてモバイル表示を判定
  const shouldShowMobile =
    !disableMobileView && (breakpoint === 'sm' ? isSmallMobile : isMobile);

  // モバイルカードリスト表示
  if (shouldShowMobile) {
    // pagination を MobileCardList 用に変換
    const mobilePagination = pagination
      ? typeof pagination === 'object'
        ? {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: pagination.onChange,
          }
        : undefined
      : undefined;

    return (
      <MobileCardList
        columns={columns}
        dataSource={[...(dataSource || [])]}
        mobileCardConfig={mobileCardConfig}
        loading={typeof loading === 'boolean' ? loading : !!loading}
        rowKey={rowKey as keyof T | ((record: T) => string) | undefined}
        rowSelection={rowSelection}
        pagination={mobilePagination}
      />
    );
  }

  // デスクトップテーブル表示
  // hidden ロールのカラムはデスクトップでも表示（操作列など）
  const desktopColumns = columns;

  // スクロール設定
  const calculatedScrollX = scrollX ?? calculateScrollX(columns);
  const scroll = {
    x: calculatedScrollX,
    ...tableProps.scroll,
  };

  return (
    <Table
      {...tableProps}
      columns={desktopColumns}
      dataSource={dataSource}
      loading={loading}
      rowKey={rowKey}
      rowSelection={rowSelection}
      pagination={pagination}
      scroll={scroll}
    />
  );
}
